import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import NumberInput from "@cayuse/shared-react.components.form.inputs.number";
import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import Dropdown from "components/Dropdown";
import PercentInput from "components/PercentInput";
import UnitSearchInput from "components/UnitSearchInput";
import { useDictionary } from "hooks";
import { picklistsSelectors } from "store/selectors";

import styles from "./styles.css";


export const AddUnnamedMember = ({ setUserData, userData }) => {
	const dictionary = useDictionary("budgetBuilder.components.AddUnnamedMember");

	const { options } = useSelector(picklistsSelectors.researchTeamRoles);

	const researchTeamRoles = useMemo(
		() => options.map(role => ({ text: role.text, value: role.id })),
		[ options ],
	);

	const onRoleChange = roleId => {
		if (isNonEmptyArray(researchTeamRoles)) {
			for (let role of researchTeamRoles) {
				if (role.value === roleId) {
					setUserData({ ...userData, role: roleId, roleLabel: role.text });
					break;
				}
			}
		}
	};

	const onInternalAssociationChange = useCallback(
		association => setUserData(prev => ({ ...prev, association })),
		[],
	);

	const handleChange = useCallback(
		newUserData => setUserData(prev => ({ ...prev, ...newUserData })),
		[],
	);

	return (
		<div className="ui grid">
			<div className="row">
				<div className="six wide column">
					<label>{dictionary.role || "Role"}</label>
					<Dropdown
						fluid
						selection
						placeholder={dictionary.role || "Role"}
						options={researchTeamRoles}
						onChange={(event, { value }) => onRoleChange(value)}
						value={userData.role}
					/>
				</div>
				<div className="six wide column">
					<label>{dictionary.internalAssociation || "Internal Association"}</label>
					<UnitSearchInput
						selection
						placeholder={dictionary.internalAssociation || "Internal Association"}
						onChange={onInternalAssociationChange}
						value={userData.association}
					/>
				</div>
			</div>
			<div className="row">
				<div className="six wide column">
					<label>{dictionary.calendarAppointmentMonths || "Calendar Appointment Months"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.calendarMonths}
						onChange={event => handleChange({ calendarMonths: event.target.value })}
					/>
				</div>
				<div className="six wide column">
					<label>{dictionary.calendarSalary || "Calendar Salary"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.calendarSalary}
						onChange={event => handleChange({ calendarSalary: event.target.value })}
					/>
				</div>
				<div className="four wide column">
					<label>{dictionary.calendarFringeRate || "Calendar Fringe Rate"}</label>
					<PercentInput
						value={userData.calendarFringeRate}
						onChange={calendarFringeRate => handleChange({ calendarFringeRate })}
					/>
				</div>
			</div>
			<div className="row">
				<div className="six wide column">
					<label>{dictionary.academicAppointmentMonths || "Academic Appointment Months"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.academicMonths}
						onChange={event => handleChange({ academicMonths: event.target.value })}
					/>
				</div>
				<div className="six wide column">
					<label>{dictionary.academicSalary || "Academic Salary"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.academicSalary}
						onChange={event => handleChange({ academicSalary: event.target.value })}
					/>
				</div>
				<div className="four wide column">
					<label>{dictionary.academicFringeRate || "Academic Fringe Rate"}</label>
					<PercentInput
						value={userData.academicFringeRate}
						onChange={academicFringeRate => handleChange({ academicFringeRate })}
					/>
				</div>
			</div>
			<div className="row">
				<div className="six wide column">
					<label>{dictionary.summerAppointmentMonths || "Summer Appointment Months"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.summerMonths}
						onChange={event => handleChange({ summerMonths: event.target.value })}
					/>
				</div>
				<div className="six wide column">
					<label>{dictionary.summerSalary || "Summer Salary"}</label>
					<NumberInput
						className={styles.fullWidth}
						value={userData.summerSalary}
						onChange={event => handleChange({ summerSalary: event.target.value })}
					/>
				</div>
				<div className="four wide column">
					<label>{dictionary.summerFringeRate || "Summer Fringe Rate"}</label>
					<PercentInput
						value={userData.summerFringeRate}
						onChange={summerFringeRate => handleChange({ summerFringeRate })}
					/>
				</div>
			</div>
		</div>
	);
};
